<div class="navigation-header" [class.quickstart]="quickstart()">
  @if (quickstart()) {
    <dt-tag-input [allTags]="tags()" [(selectedTagIds)]="filterTagIds" [slim]="true" (selectedTagIdsChange)="changedTagFilter()" i18n
      >Nach Tags filtern</dt-tag-input
    >
  } @else {
    <button mat-icon-button (click)="changeFolder(currentFolder().parent)" [disabled]="!currentFolder().parent" data-cy="move-up-button">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div class="folder-hierarchy-container">
      <div class="folder-hierarchy">
        @for (hierarchyElement of parentHierarchy(); track hierarchyElement.id) {
          <span
            (click)="changeFolder(hierarchyElement)"
            matRipple
            (drop)="dropOnElement($event, hierarchyElement)"
            (dragover)="dragOverElement($event, hierarchyElement)"
            (dragenter)="dragEnterElement($event, hierarchyElement)"
            (dragleave)="dragLeaveElement($event, hierarchyElement)"
            [class.dragged-over]="currentlyDraggingInto === hierarchyElement"
            data-cy="folder-hierarchy-element"
          >
            {{ hierarchyElement.name }}
          </span>
          @if (hierarchyElement.folderChildren.length > 0) {
            <mat-icon
              color="primary"
              [inline]="true"
              [matMenuTriggerFor]="nextFolderMenu"
              matRipple
              data-cy="folder-navigation-menu-trigger"
              >chevron_right</mat-icon
            >

            <mat-menu #nextFolderMenu>
              @for (nextFolder of hierarchyElement.folderChildren; track nextFolder.id) {
                <button mat-menu-item (click)="changeFolder(nextFolder)" data-cy="folder-navigation-button">
                  {{ nextFolder.name }}
                </button>
              }
            </mat-menu>
          }
        }
      </div>
    </div>
    <button #newFolderButton mat-stroked-button color="primary" (click)="openNewFolderPopover()" data-cy="new-folder-button">
      <mat-icon>add_circle</mat-icon>
      <ng-container i18n>Neuer Ordner</ng-container>
      <ng-template #newFolderTemplate let-close="close">
        <form class="new-folder-container" (submit)="createFolderInCurrentFolder(folderNameInput.value); close()">
          <mat-form-field [subscriptSizing]="'dynamic'">
            <mat-label i18n>Name des Ordners</mat-label>
            <input #folderNameInput matInput dtAutoFocus data-cy="folder-name-input" />
          </mat-form-field>
          <button mat-raised-button color="accent" [disabled]="!folderNameInput.value" type="submit" data-cy="new-folder-button-confirm">
            <mat-icon>add</mat-icon>
            <span i18n>Erstellen</span>
          </button>
        </form>
      </ng-template>
    </button>
  }
  <mat-form-field class="search-field" subscriptSizing="dynamic" appearance="outline">
    <mat-icon matPrefix>search</mat-icon>
    <mat-label i18n>Nach Name suchen</mat-label>
    <input
      matInput
      dtAutoFocus
      type="text"
      [(ngModel)]="filterText"
      [ngModelOptions]="{ standalone: true }"
      data-cy="project-search-input"
    />
    @if (filterText()) {
      <button mat-icon-button matSuffix type="button" (click)="clearFilterText()"><mat-icon>clear</mat-icon></button>
    }
  </mat-form-field>
</div>

<div class="table-container">
  <table
    #table
    mat-table
    [dataSource]="dataSource"
    matSort
    [matSortDisableClear]="true"
    [trackBy]="trackTableElements"
    matSortActive="type"
    matSortDirection="asc"
    cdkDropListGroup
    data-cy="project-list"
    [class.quickstart]="quickstart()"
  >
    <ng-container matColumnDef="type">
      <th mat-header-cell *matHeaderCellDef i18n mat-sort-header>Typ</th>
      <td mat-cell *matCellDef="let element">
        @if (isFolderNode(element)) {
          <mat-icon [color]="'accent'">folder</mat-icon>
        } @else if (isTeamProjectNode(element)) {
          <mat-icon [color]="'primary'">groups</mat-icon>
        } @else {
          <mat-icon [color]="'basic'">description</mat-icon>
        }
      </td>
    </ng-container>
    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef i18n mat-sort-header>Name</th>
      <td mat-cell *matCellDef="let element">
        <div class="name-wrapper">
          <span #elementName data-cy="project-name">{{ element.name }}</span>
          @if (currentlyRenamingElement === element) {
            <div class="rename-container">
              <input
                #renameInput
                matInput
                [dtAutoFocus]="true"
                [dtAutoFocusSelectAll]="true"
                [value]="element.name"
                (click)="$event.stopPropagation()"
                (blur)="renameElement(renameInput.value, elementName)"
                (keyup.enter)="renameElement(renameInput.value, elementName)"
                (keydown.escape)="currentlyRenamingElement = null; $event.stopPropagation()"
                data-cy="rename-input"
              />
            </div>
          }

          @if (isTeamProjectNode(element)) {
            <span class="role-in-team" data-cy="role-in-team" i18n>{{ element.value.userMemberRole | teamMemberRole }}</span>
          }
        </div>
      </td>
    </ng-container>
    <ng-container matColumnDef="path">
      <th mat-header-cell *matHeaderCellDef i18n>Pfad</th>
      <td mat-cell *matCellDef="let element">
        <span class="project-path">{{ getDisplayPath(element) }}</span>
      </td>
    </ng-container>
    <ng-container matColumnDef="tags">
      <th mat-header-cell *matHeaderCellDef i18n>Tags</th>
      <td #cell mat-cell *matCellDef="let element">
        @if (tagsById) {
          @defer (on viewport(cell)) {
            <dt-tag-list
              [tagIds]="element.value.tags"
              [tagsById]="tagsById()"
              [selectedTagIds]="filterTagIds()"
              (tagClick)="onTagClick($event)"
              singleRow
            ></dt-tag-list>
          }
        }
      </td>
    </ng-container>
    <ng-container matColumnDef="date">
      <th mat-header-cell *matHeaderCellDef i18n mat-sort-header="updatedAt">Bearbeitet am</th>
      <td mat-cell *matCellDef="let element">{{ element.updatedAt | date: 'medium' }}</td>
    </ng-container>
    <ng-container matColumnDef="quickactions">
      <th mat-header-cell *matHeaderCellDef></th>
      <td #cell mat-cell *matCellDef="let element">
        @defer (on viewport) {
          <div #quickActionsContainer class="quickactions-container">
            @if (canBeRenamed(element)) {
              <button
                mat-icon-button
                (click)="currentlyRenamingElement = element; $event.stopPropagation()"
                i18n-matTooltip
                [matTooltip]="'Umbenennen'"
                [matTooltipShowDelay]="500"
              >
                <mat-icon>drive_file_rename_outline</mat-icon>
              </button>
            }

            @if (canBeDeleted(element)) {
              <button
                mat-icon-button
                (click)="deleteElement(element, quickActionsContainer); $event.stopPropagation()"
                i18n-matTooltip
                [matTooltip]="'Löschen'"
                [matTooltipShowDelay]="500"
              >
                <mat-icon>delete</mat-icon>
              </button>
            }

            @if (isQuickstartProjectNode(element)) {
              <button
                mat-icon-button
                i18n-title
                title="Link in die Zwischenablage kopieren"
                [cdkCopyToClipboard]="element.value.shareUrl"
                (click)="$event.stopPropagation()"
              >
                <mat-icon>file_copy</mat-icon>
              </button>
            }
          </div>
        } @placeholder {
          <div></div>
        }
      </td>
    </ng-container>
    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let element">
        @defer (on viewport) {
          <button
            #actionButton
            mat-icon-button
            [matMenuTriggerFor]="actionMenu"
            i18n-matTooltip
            matTooltip="Weitere Aktionen"
            (click)="$event.stopPropagation()"
            data-cy="element-actions"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #actionMenu>
            @if (!quickstart()) {
              <button mat-menu-item [matMenuTriggerFor]="folderTreeMenu.menu()" data-cy="move-element-trigger">
                <mat-icon>add</mat-icon>
                <span i18n>In Ordner verschieben</span>
                <dt-tree-menu
                  #folderTreeMenu
                  [items]="projectHierarchy().root.folderChildren"
                  [childPropertyName]="'folderChildren'"
                  [itemNameProperty]="'name'"
                  [ignoreElement]="element"
                  (itemClicked)="moveIntoFolder(element, $event, actionButton._elementRef.nativeElement)"
                >
                  <button
                    mat-menu-item
                    (click)="moveIntoFolder(element, projectHierarchy().root, actionButton._elementRef.nativeElement)"
                    class="root-folder-item"
                    data-cy="move-to-folder-button"
                  >
                    <mat-icon [color]="'primary'">folder</mat-icon>
                    <ng-container i18n>Meine Projekte</ng-container>
                  </button>
                </dt-tree-menu>
              </button>
            }

            @if (canBeRenamed(element)) {
              <button mat-menu-item (click)="currentlyRenamingElement = element" data-cy="rename-element-button">
                <mat-icon>drive_file_rename_outline</mat-icon>
                <span i18n>Umbenennen</span>
              </button>
            }

            @if (isProjectNode(element) && !isQuickstartProjectNode(element)) {
              <button mat-menu-item (click)="loadProject(element, true)" data-cy="load-as-copy-button">
                <mat-icon>file_copy</mat-icon>
                <span i18n>Als Kopie öffnen</span>
              </button>

              @if (isTeamProjectNode(element)) {
                <button mat-menu-item (click)="leaveTeam(element, actionButton._elementRef.nativeElement)">
                  <mat-icon>logout</mat-icon>
                  <span i18n>Team verlassen</span>
                </button>
              } @else {
                <button mat-menu-item (click)="createProjectLink(element)" data-cy="share-project-button">
                  <mat-icon>share</mat-icon>
                  <span i18n>Teilen</span>
                </button>
                <button mat-menu-item (click)="createTeam(element, actionButton._elementRef.nativeElement)">
                  <mat-icon>groups</mat-icon>
                  <span i18n>Teamprojekt aus Vorlage erstellen</span>
                </button>
              }
            }

            @if (canBeDeleted(element)) {
              <button
                mat-menu-item
                (click)="deleteElement(element, actionButton._elementRef.nativeElement)"
                data-cy="delete-element-button"
              >
                <mat-icon>delete</mat-icon>
                <span i18n>Löschen</span>
              </button>
            }
          </mat-menu>
        } @placeholder {
          <div></div>
        }
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
    <tr
      mat-row
      draggable="true"
      *matRowDef="let row; columns: displayedColumns()"
      class="hierarchy-row"
      (click)="clickRow(row)"
      [class.new]="newElementId() === row.id"
      [class.dragging-over]="currentlyDraggingInto === row"
      [class.dragging]="currentlyDragging === row"
      [class.loaded]="loadedHierarchyIds().has(row.id)"
      [class.highlighted]="highlightedProjectName() === row.name"
      (drop)="dropOnElement($event, row)"
      (dragover)="dragOverElement($event, row)"
      (dragstart)="dragElement($event, row)"
      (dragenter)="dragEnterElement($event, row)"
      (dragleave)="dragLeaveElement($event, row)"
      (dragend)="stopDrag()"
      (mouseenter)="newElementId() === row.id && newElementId.set(null)"
      data-cy="project-hierarchy-row"
    ></tr>
    <tr class="mat-row" *matNoDataRow>
      <td class="mat-cell" [attr.colspan]="displayedColumns().length">
        @if (!userHasElements()) {
          @if (quickstart()) {
            <ng-container i18n>Es sind aktuell keine Themenprojekte verfügbar.</ng-container>
          } @else {
            <ng-container i18n>Du hast noch kein Projekt online gespeichert.</ng-container>
          }
        } @else if (filterText()) {
          <ng-container i18n>Unter dem gesuchten Namen wurde kein Projekt gefunden.</ng-container>
        }
      </td>
    </tr>
  </table>
</div>
